/**
 * 产品首页内容管理 - Mock 数据层
 * localStorage 持久化模拟，包含草稿/已发布双表存储
 */

import type {
  PageSection, HeroContent, FeatureCard, DataStat, Testimonial,
  DocEntry, NewsEntry, ProcessStep, BoardColumn, WorkScenario,
  CtaContent, FooterLinkGroup, SeoMetadata,
  LandingPageData,
} from '@/types/landingPage'
import {
  featureCards as defaultFeatures,
  dataStats as defaultStats,
  bottomStat as defaultPrimaryStat,
  testimonials as defaultTestimonials,
  docEntries as defaultDocEntries,
  newsEntries as defaultNewsEntries,
  processSteps as defaultProcessSteps,
  scenarios as defaultScenarios,
  boardColumns as defaultBoardColumns,
  footerColumns as defaultFooterColumns,
} from '@/mock/landing'
import { storageKey } from '@/services/types'
import { SECTION_KEY } from '@/constants/landingSections'

const DRAFT_KEY = storageKey('landing_draft')
const PUBLISHED_KEY = storageKey('landing_published')
const SECTIONS_KEY = storageKey('landing_sections')
const SEO_KEY = storageKey('landing_seo')

function defaultHero(): HeroContent {
  return {
    headline: '工作做一遍，台账自动生成',
    subheadline: '让过程本身就是台账。',
    primaryBtnText: '免费试用',
    primaryBtnLink: '/login',
    secondaryBtnText: '查看场景',
    secondaryBtnLink: '#scenarios',
    trustText: ['无需信用卡', '3 分钟上手', '免费体验全部功能'],
  }
}

function defaultCta(): CtaContent {
  return {
    title: '让工作过程自己变成台账',
    subtitle: '免费试用，立即体验"过程即台账"',
    buttonText: '免费试用',
    buttonLink: '/login',
    mobileButtonText: '立即免费试用',
  }
}

function defaultFooterGroups(): FooterLinkGroup[] {
  return defaultFooterColumns.map(col => ({
    title: col.title,
    links: col.links.map(l => ({ label: l.label, href: l.href })),
  }))
}

function defaultSeo(): SeoMetadata {
  return {
    pageTitle: '智台账 - 基层工作全过程数字化记录与管理平台',
    pageDescription: 'AI赋能台账编制，让过程即台账成为现实。支持一键理解上级文件、自动生成完整台账套件、多模态生成高质量宣传稿件。',
    keywords: ['智台账', '台账管理', '基层工作', '数字化台账', 'AI台账'],
  }
}

function defaultSections(): PageSection[] {
  // key 一律引用 SECTION_KEY(与后端 page_sections.section_key 契约一致,
  // 见 constants/landingSections)——此前自造 process-flow/kanban/docs-news
  // 导致 mock 模式下公开首页 3 区块不渲染
  return [
    { key: SECTION_KEY.hero, name: 'Hero 区', icon: 'LayoutDashboard', status: 'published', sortOrder: 1, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.features, name: '核心功能展示', icon: 'Grid3x3', status: 'published', sortOrder: 2, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.process, name: '全流程可视化', icon: 'GitBranch', status: 'published', sortOrder: 3, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.board, name: '任务看板预览', icon: 'KanbanSquare', status: 'published', sortOrder: 4, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.stats, name: '数据佐证', icon: 'BarChart3', status: 'published', sortOrder: 5, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.scenarios, name: '场景化演示', icon: 'PlayCircle', status: 'published', sortOrder: 6, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.testimonials, name: '用户证言', icon: 'MessageSquareQuote', status: 'published', sortOrder: 7, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.docs, name: '文档中心', icon: 'BookOpen', status: 'published', sortOrder: 8, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.news, name: '活动资讯', icon: 'Megaphone', status: 'published', sortOrder: 9, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.cta, name: 'CTA 区', icon: 'Target', status: 'published', sortOrder: 10, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.footer, name: 'Footer', icon: 'RectangleEllipsis', status: 'published', sortOrder: 11, lastModified: '', lastModifiedBy: '' },
    { key: SECTION_KEY.seo, name: 'SEO 元数据', icon: 'Search', status: 'published', sortOrder: 12, lastModified: '', lastModifiedBy: '' },
  ]
}

function defaultData(): LandingPageData {
  return {
    hero: defaultHero(),
    features: defaultFeatures,
    featureSortOrder: defaultFeatures.map(f => f.id),
    stats: defaultStats,
    primaryStat: defaultPrimaryStat,
    testimonials: defaultTestimonials,
    testimonialSortOrder: defaultTestimonials.map(t => t.id),
    docEntries: defaultDocEntries,
    docSortOrder: defaultDocEntries.map(d => d.id),
    newsEntries: defaultNewsEntries,
    newsSortOrder: defaultNewsEntries.map(n => n.id),
    processSteps: defaultProcessSteps,
    scenarios: defaultScenarios,
    scenarioSortOrder: defaultScenarios.map(s => s.id),
    boardColumns: defaultBoardColumns,
    cta: defaultCta(),
    footerGroups: defaultFooterGroups(),
    seo: defaultSeo(),
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, data: unknown): void {
  localStorage.setItem(key, JSON.stringify(data))
}

// ====== Sections ======

export function getSections(): PageSection[] {
  return readJson(SECTIONS_KEY, defaultSections())
}

export function updateSections(sections: PageSection[]): void {
  writeJson(SECTIONS_KEY, sections)
}

// ====== Published Data ======

export function getPublishedData(): LandingPageData {
  return readJson(PUBLISHED_KEY, defaultData())
}

// ====== Draft Data ======

export function getDraftData(): LandingPageData | null {
  const raw = localStorage.getItem(DRAFT_KEY)
  return raw ? JSON.parse(raw) as LandingPageData : null
}

function saveDraftRaw(data: LandingPageData): void {
  writeJson(DRAFT_KEY, data)
}

export function ensureDraft(): LandingPageData {
  const existing = getDraftData()
  if (existing) return existing
  const published = getPublishedData()
  const draft = JSON.parse(JSON.stringify(published)) as LandingPageData
  saveDraftRaw(draft)
  return draft
}

export function saveDraftHero(content: HeroContent): void {
  const draft = ensureDraft()
  draft.hero = content
  saveDraftRaw(draft)
}

export function saveDraftFeatures(features: FeatureCard[], sortOrder: string[]): void {
  const draft = ensureDraft()
  draft.features = features
  draft.featureSortOrder = sortOrder
  saveDraftRaw(draft)
}

export function saveDraftStats(stats: DataStat[], primaryStat: DataStat): void {
  const draft = ensureDraft()
  draft.stats = stats
  draft.primaryStat = primaryStat
  saveDraftRaw(draft)
}

export function saveDraftTestimonials(testimonials: Testimonial[], sortOrder: string[]): void {
  const draft = ensureDraft()
  draft.testimonials = testimonials
  draft.testimonialSortOrder = sortOrder
  saveDraftRaw(draft)
}

export function saveDraftDocEntries(entries: DocEntry[], sortOrder: string[]): void {
  const draft = ensureDraft()
  draft.docEntries = entries
  draft.docSortOrder = sortOrder
  saveDraftRaw(draft)
}

export function saveDraftNewsEntries(entries: NewsEntry[], sortOrder: string[]): void {
  const draft = ensureDraft()
  draft.newsEntries = entries
  draft.newsSortOrder = sortOrder
  saveDraftRaw(draft)
}

export function saveDraftProcessSteps(steps: ProcessStep[]): void {
  const draft = ensureDraft()
  draft.processSteps = steps
  saveDraftRaw(draft)
}

export function saveDraftScenarios(scenarios: WorkScenario[], sortOrder: string[]): void {
  const draft = ensureDraft()
  draft.scenarios = scenarios
  draft.scenarioSortOrder = sortOrder
  saveDraftRaw(draft)
}

export function saveDraftBoardColumns(columns: BoardColumn[]): void {
  const draft = ensureDraft()
  draft.boardColumns = columns
  saveDraftRaw(draft)
}

export function saveDraftCta(content: CtaContent): void {
  const draft = ensureDraft()
  draft.cta = content
  saveDraftRaw(draft)
}

export function saveDraftFooter(groups: FooterLinkGroup[]): void {
  const draft = ensureDraft()
  draft.footerGroups = groups
  saveDraftRaw(draft)
}

export function saveDraftSeo(seo: SeoMetadata): void {
  const draft = ensureDraft()
  draft.seo = seo
  saveDraftRaw(draft)
}

// ====== Publish ======

export function publishDraft(): LandingPageData {
  const draft = getDraftData()
  const data = draft || getPublishedData()
  writeJson(PUBLISHED_KEY, data)
  // Clear draft after publish
  localStorage.removeItem(DRAFT_KEY)
  return data
}

// ====== SEO ======

export function getSeoData(): SeoMetadata {
  return readJson(SEO_KEY, defaultSeo())
}

export function saveSeoData(seo: SeoMetadata): void {
  writeJson(SEO_KEY, seo)
}
